import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import http, { fetchTrip, fetchTripPiv } from '../api/client';
import { getComposition, TRAIN_TYPES } from '../data/mock';
import { ArrowLeft, ChevronRight, Loader2, MapPin, AlertTriangle, Train as TrainIcon, Accessibility, Bike, Map as MapIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import TrainMap from '../components/TrainMap';
import { useGrandEstStations } from '../hooks/useGrandEstStations';
import { useFluoVehicles } from '../hooks/useFluoVehicles';

const Coach = ({ coach }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center min-w-[110px]" data-testid={`coach-${coach.num}`}>
      <div className="text-[11px] text-slate-500 mb-1">{t('train.coach_num', { n: coach.num })}</div>
      <div className="relative w-[110px] h-[64px] rounded-xl border-2 border-ter-blue overflow-hidden bg-white shadow-sm">
        <div className="absolute inset-x-3 top-2 bottom-2 grid grid-cols-4 gap-1">
          {[0,1,2,3].map(i => <div key={i} className="rounded-sm bg-slate-100 border border-slate-200" />)}
        </div>
        <div className="absolute left-1 top-1 bottom-1 w-1 rounded bg-ter-blue/40" />
        <div className="absolute right-1 top-1 bottom-1 w-1 rounded bg-ter-blue/40" />
        <div className="absolute top-1 right-1 text-[9px] font-extrabold text-ter-blue bg-white/90 rounded px-1">{coach.class}ᵉ</div>
      </div>
      <div className="flex items-center gap-1 mt-1">
        {coach.pmr && <Accessibility className="w-3.5 h-3.5 text-ter-blue" />}
        {coach.bike && <Bike className="w-3.5 h-3.5 text-emerald-600" />}
        <span className="text-[10px] tabular-nums text-slate-600 font-semibold">{t('train.coach_seats', { n: coach.seats })}</span>
      </div>
    </div>
  );
};

const Itinerary = ({ stops, isCar }) => {
  const { t } = useTranslation();
  if (!stops || stops.length === 0) {
    return <div className="p-6 text-center text-sm text-slate-500" data-testid="train-no-stops">{t('train.no_stops')}</div>;
  }
  return (
    <ol className="relative" data-testid="train-itinerary-list">
      {stops.map((stop, idx) => {
        const last = idx === stops.length - 1;
        const delay = stop.delay_departure ?? stop.delay_arrival ?? 0;
        const time = stop.departure || stop.arrival || '—';
        return (
          <li key={idx} className="flex gap-4">
            <div className="flex flex-col items-center">
              <div className={`w-3 h-3 rounded-full ${idx === 0 || last ? 'bg-ter-red' : 'bg-ter-blue'} ring-4 ring-white shadow`} />
              {!last && <div className="flex-1 w-0.5 bg-slate-200 my-1" />}
            </div>
            <div className="pb-5 flex-1">
              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <div className="stop-name font-bold text-slate-900 leading-tight">{stop.name}</div>
                <div className="flex items-center gap-3 text-xs text-slate-500">
                  {stop.platform && (
                    <span className="px-2 py-0.5 rounded bg-slate-100 font-semibold text-ter-blue">
                      {isCar ? 'BUS' : `${t('board.platform_label')} ${stop.platform}`}
                    </span>
                  )}
                  <span className="stop-time font-mono font-bold text-base text-ter-blue tabular-nums">{time}</span>
                  {delay > 0 && <span className={`font-bold tabular-nums ${delay >= 10 ? 'text-red-600' : 'text-orange-500'}`}>+{delay}min</span>}
                </div>
              </div>
              {stop.arrival && stop.departure && stop.arrival !== stop.departure && (
                <div className="text-[11px] text-slate-500 mt-0.5">arr. {stop.arrival} • dép. {stop.departure}</div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

const TrainDetail = () => {
  const { vjId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const number = params.get('number') || '';
  const status = params.get('status') || 'on_time';
  const delayMin = parseInt(params.get('delay') || '0', 10) || 0;
  const marqueParam = params.get('marque') || '';
  const modeTransportParam = params.get('modeTransport') || '';
  const { isGrandEst } = useGrandEstStations();
  const { isFluoCar, getFluoBadgeUrl, getFluoCarSeats, loaded: fluoLoaded } = useFluoVehicles();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [composition, setComposition] = useState(null);
  const [isPiv, setIsPiv] = useState(false);
  const [compositionIsMock, setCompositionMock] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    setTrip(null);
    setComposition(null);
    setIsPiv(false);

    const run = async () => {
      try {
        const cfgRes = await http.get('/footer-config');
        if (!alive) return;
        const prov = cfgRes.data?.provider || 'navitia';
        const today = new Date().toISOString().split('T')[0];

        if (prov === 'piv' && number) {
          setIsPiv(true);
          const data = await fetchTripPiv(number, today);
          if (!alive) return;
          setTrip(data);
          const mockComp = getComposition(number);
          if (data.composition && data.composition.wagonsCount > 0) {
            const seatsFromMock = data.composition.totalSeats === 0;
            setComposition({
              ...data.composition,
              totalSeats: data.composition.totalSeats > 0
                ? data.composition.totalSeats
                : (mockComp?.totalSeats || 0),
            });
            setCompositionMock(seatsFromMock);
          } else {
            setComposition({
              ...mockComp,
              libelle:     data.composition?.libelle     || mockComp?.type || '',
              serie:       data.composition?.serie       || '',
              equipements: data.composition?.equipements || [],
            });
            setCompositionMock(true);
          }
        } else {
          const data = await fetchTrip(vjId);
          if (!alive) return;
          setTrip(data);
          setComposition(getComposition(number));
          setCompositionMock(true);
        }
      } catch (err) {
        if (alive) setError(err?.response?.data?.detail || err.message);
      } finally {
        if (alive) setLoading(false);
      }
    };

    run();
    return () => { alive = false; };
  }, [vjId, number]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="train-detail-page">
      <nav className="flex items-center gap-2 text-sm text-slate-500 mb-4">
        <span className="hover:text-ter-blue cursor-pointer" onClick={() => navigate('/')} data-testid="train-crumb-home">{t('nav.home')}</span>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="font-semibold text-ter-blue" data-testid="train-crumb-label">{t('train.crumb')}</span>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="font-semibold text-slate-700">{number || vjId}</span>
      </nav>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-4 sm:px-6 py-4 bg-gradient-to-r from-[#191998] to-[#4444CC] text-white flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" data-testid="train-back-btn" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4" /></Button>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-white/70">{isFluoCar(number) ? 'Bus' : 'Train'}</div>
              <div className="text-2xl font-extrabold flex items-center gap-2">
                {(() => {
                  const marque = marqueParam || trip?.marque || '';
                  const marqueUp = marque.toUpperCase();
                  const isHighSpeed = (modeTransportParam || trip?.modeTransport) === 'highSpeedRail';
                  const isTGV = isHighSpeed || marqueUp.includes('TGV') || marqueUp.includes('INOUI') || marqueUp === 'OUI';
                  const involvesGrandEst =
                    isGrandEst(trip?.origine) || isGrandEst(trip?.destination) ||
                    isGrandEst(trip?.stops?.[0]?.name) ||
                    isGrandEst(trip?.stops?.[trip.stops.length - 1]?.name);

                  if (marqueUp === 'OUIGO') return <span className="inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-extrabold bg-pink-600 text-white">OUIGO</span>;
                  if (isTGV) return <span className="inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-extrabold bg-red-700 text-white">{marque || 'TGV'}</span>;
                  if (marqueUp.includes('IC')) return <span className="inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-extrabold bg-slate-600 text-white">{marque}</span>;
                  if (!marque && involvesGrandEst) {
                    if (fluoLoaded) {
                      const badgeUrl = getFluoBadgeUrl(number);
                      if (badgeUrl) return <img src={badgeUrl} alt={isFluoCar(number) ? 'Car Fluo' : 'Train Fluo'} className="h-5 object-contain" />;
                    }
                    return <span className="inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-extrabold bg-ter-yellow text-ter-blue">Fluo</span>;
                  }
                  if (!marque) return <span className="inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-extrabold bg-slate-500 text-white">TER</span>;
                  return <span className="inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-extrabold bg-ter-yellow text-ter-blue">{marque}</span>;
                })()}
                {number || trip?.name || '—'}
              </div>
              {trip?.stops?.length > 0 && (
                <div className="text-sm text-white/80">{trip.stops[0]?.name} → {trip.stops[trip.stops.length - 1]?.name}</div>
              )}
            </div>
          </div>
        </div>

        <Tabs defaultValue="itinerary" className="px-4 sm:px-6 pt-5">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="itinerary" className="font-bold tab-fluo" data-testid="tab-route">
              <MapPin className="w-4 h-4 mr-1.5" /> {t('train.tab_route')}
            </TabsTrigger>
            <TabsTrigger value="map" className="font-bold tab-fluo" data-testid="tab-map">
              <MapIcon className="w-4 h-4 mr-1.5" /> {t('train.tab_map')}
            </TabsTrigger>
            <TabsTrigger value="composition" className="font-bold tab-fluo" data-testid="tab-composition">
              <TrainIcon className="w-4 h-4 mr-1.5" /> {t('train.tab_composition')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="itinerary" className="py-5">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold" data-testid="train-real-data-badge">✓ {t('train.real_data_badge')}</span>
                <span className="text-xs text-slate-500 ml-1">{isPiv ? 'Source : SNCF PIV' : t('train.source_navitia')}</span>
              </div>
              {trip?.stops && <div className="text-sm text-slate-500" data-testid="train-stops-count">{t('train.stops_count', { count: trip.stops.length })}</div>}
            </div>
            {loading && (
              <div className="flex items-center justify-center py-8 gap-2 text-slate-500" data-testid="train-loading">
                <Loader2 className="w-5 h-5 animate-spin" /> {t('train.loading_route')}
              </div>
            )}
            {error && !loading && (
              <div className="p-4 rounded-md bg-red-50 border border-red-200 text-sm text-red-800 flex items-start gap-2" data-testid="train-error">
                <AlertTriangle className="w-4 h-4 mt-0.5" /> {error}
              </div>
            )}
            {!loading && !error && trip && <Itinerary stops={trip.stops} isCar={fluoLoaded && isFluoCar(number)} />}
          </TabsContent>

          <TabsContent value="map" className="py-5">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold">✓ {t('train.real_data_badge')}</span>
                <span className="text-xs text-slate-500 ml-1">{isPiv ? 'Source : SNCF PIV' : t('train.map_sources')}</span>
              </div>
              {trip?.stops && <div className="text-sm text-slate-500">{t('train.stops_count', { count: trip.stops.length })}</div>}
            </div>
            {loading && (
              <div className="flex items-center justify-center py-8 gap-2 text-slate-500">
                <Loader2 className="w-5 h-5 animate-spin" /> {t('train.loading_route')}
              </div>
            )}
            {error && !loading && (
              <div className="p-4 rounded-md bg-red-50 border border-red-200 text-sm text-red-800 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5" /> {error}
              </div>
            )}
            {!loading && !error && trip && <TrainMap stops={trip.stops} trainNumber={number} status={status} delayMin={delayMin} />}
          </TabsContent>

          <TabsContent value="composition" className="py-5">
            {compositionIsMock && (
              <div className="mb-4 p-3 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900 flex items-start gap-2" data-testid="train-comp-disclaimer">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <div className="font-bold mb-0.5">{t('train.comp_disclaimer_title')}</div>
                  {t('train.comp_disclaimer_text')}
                </div>
              </div>
            )}

            {fluoLoaded && isFluoCar(number) ? (
              <div data-testid="train-composition-block">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                  <div className="rounded-xl bg-slate-50 p-3">
                    <div className="text-[11px] uppercase tracking-wider text-slate-500">Véhicule</div>
                    <div className="text-base font-extrabold text-ter-blue">1 bus</div>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-3">
                    <div className="text-[11px] uppercase tracking-wider text-slate-500">{t('train.seats')}</div>
                    <div className="text-2xl font-extrabold text-ter-blue tabular-nums">{getFluoCarSeats(number)}</div>
                  </div>
                </div>
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-8 flex flex-col items-center justify-center gap-2">
                  <span className="text-4xl">🚌</span>
                  <span className="font-bold text-slate-700">BUS</span>
                  <span className="text-sm text-slate-500">{getFluoCarSeats(number)} places assises</span>
                </div>
              </div>
            ) : composition ? (
              <div data-testid="train-composition-block">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                  <div className="rounded-xl bg-slate-50 p-3">
                    <div className="text-[11px] uppercase tracking-wider text-slate-500">{t('train.coaches')}</div>
                    <div className="text-2xl font-extrabold text-ter-blue tabular-nums">{composition.wagonsCount}</div>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-3">
                    <div className="text-[11px] uppercase tracking-wider text-slate-500">{t('train.seats')}</div>
                    <div className="text-2xl font-extrabold text-ter-blue tabular-nums">{composition.totalSeats > 0 ? composition.totalSeats : <span className="text-sm text-slate-400 italic">{t('composition.seats_unavailable')}</span>}</div>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-3">
                    <div className="text-[11px] uppercase tracking-wider text-slate-500">{t('train.material')}</div>
                    <div className="text-base font-extrabold text-ter-blue">{composition.libelle || composition.type}</div>
                  </div>
                  {composition.layout && (
                    <div className="rounded-xl bg-slate-50 p-3">
                      <div className="text-[11px] uppercase tracking-wider text-slate-500">{t('train.configuration')}</div>
                      <div className="text-2xl font-extrabold text-ter-blue">{composition.layout}</div>
                    </div>
                  )}
                </div>
                {composition.coaches ? (
                  <>
                    <div className="rounded-xl bg-slate-50 border border-slate-200 p-6 overflow-x-auto scrollbar-thin">
                      <div className="flex items-center gap-2 min-w-max">
                        <div className="flex flex-col items-center">
                          <div className="text-[11px] text-ter-red font-bold mb-1">{t('train.head_label')}</div>
                          <div className="w-16 h-16 rounded-l-2xl bg-ter-red text-white flex items-center justify-center shadow-md">
                            <TrainIcon className="w-8 h-8" />
                          </div>
                          <div className="text-[10px] text-slate-500 mt-1">{t('train.direction')} →</div>
                        </div>
                        <div className="flex items-end gap-2">
                          {composition.coaches.map((c) => <Coach key={c.num} coach={c} />)}
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-600">
                      <div className="flex items-center gap-1"><Accessibility className="w-4 h-4 text-ter-blue" /> {t('train.legend_pmr')}</div>
                      <div className="flex items-center gap-1"><Bike className="w-4 h-4 text-emerald-600" /> {t('train.legend_bike')}</div>
                      <div className="flex items-center gap-1"><span className="font-bold text-ter-blue">1ᵉ / 2ᵉ</span> {t('train.legend_class')}</div>
                    </div>
                  </>
                ) : composition.equipements?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {composition.equipements.map((eq) => (
                      <span key={eq.code} className="px-2 py-1 rounded bg-slate-100 text-xs font-semibold text-slate-700">{eq.libelle}</span>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="p-6 rounded-xl bg-slate-50 border border-slate-200 text-center" data-testid="train-comp-unavailable">
                <TrainIcon className="w-10 h-10 mx-auto text-slate-400 mb-3" />
                <div className="font-semibold text-slate-700 mb-1">{t('train.comp_unavailable_title')}</div>
                <p className="text-sm text-slate-500 max-w-md mx-auto">
                  {t('train.comp_unavailable_text', { number: number || vjId })}
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>
        <div className="h-4" />
      </div>
    </div>
  );
};

export default TrainDetail;
